import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import { DashboardProvider, useDashboard } from "@/lib/dataContext";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Loader } from "@/components/common/Loader";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/auth/AuthProvider";
import { LoginPage } from "@/auth/LoginPage";
import { defaultStateKey } from "@/auth/authorization";

import NationalDashboard from "@/pages/NationalDashboard";
import Overview from "@/pages/Overview";
import Trends from "@/pages/Trends";
import Membership from "@/pages/Membership";
import Activities from "@/pages/Activities";
import Youth from "@/pages/Youth";
import Leadership from "@/pages/Leadership";
import Outreach from "@/pages/Outreach";
import Districts from "@/pages/Districts";
import Samithis from "@/pages/Samithis";

const StateGate = () => {
    const { loading, error } = useDashboard();
    if (loading) return <Loader />;
    if (error)
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
                <p className="font-serif-elegant text-2xl text-foreground">Unable to load state dashboard</p>
                <p className="max-w-xl text-sm leading-6 text-muted-foreground">{error}</p>
                <a href={`${process.env.PUBLIC_URL || ""}/`} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
                    Back to dashboard
                </a>
            </div>
        );
    return (
        <Routes>
            <Route element={<DashboardLayout />}>
                <Route index element={<Overview />} />
                <Route path="/trends" element={<Trends />} />
                <Route path="/membership" element={<Membership />} />
                <Route path="/activities" element={<Activities />} />
                <Route path="/youth" element={<Youth />} />
                <Route path="/leadership" element={<Leadership />} />
                <Route path="/outreach" element={<Outreach />} />
                <Route path="/districts" element={<Districts />} />
                <Route path="/samithis" element={<Samithis />} />
            </Route>
        </Routes>
    );
};

const Application = () => {
    const location = useLocation();
    const { user } = useAuth();
    const stateKey = new URLSearchParams(location.search).get("state");
    const effectiveStateKey = stateKey || defaultStateKey(user);

    if (!effectiveStateKey) return <NationalDashboard />;

    return (
        <DashboardProvider stateKey={effectiveStateKey}>
            <StateGate />
        </DashboardProvider>
    );
};

const AuthenticatedApplication = () => {
    const { loading, user } = useAuth();
    if (loading) return <Loader label="Verifying dashboard access…" />;
    if (!user) return <LoginPage />;
    return <Application />;
};

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <AuthenticatedApplication />
                </AuthProvider>
                <Toaster position="top-right" />
            </BrowserRouter>
        </div>
    );
}

export default App;
